import {
    DollarOutlined,
    DownloadOutlined,
    EyeOutlined,
    FieldTimeOutlined,
    FileTextOutlined,
    MailOutlined,
    ReloadOutlined,
    StopOutlined,
} from "@ant-design/icons";
import { Empty, Select, Spin, Tag, Tooltip } from "antd";
import moment from "moment";
import PropTypes from "prop-types";
import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useToast } from "../../../../contexts/ToastContext";
import {
    downloadBonzahPolicyPdfFile,
    formatCurrency,
    getPolicy,
    getProviderKey,
    getSalePolicyStatus,
    isSalePaid,
    parseBlobErrorMessage,
    PROVIDER_LABELS,
} from "../../lib/insuranceSaleApi";
import useInsuranceSaleSendPaymentLink from "../../hooks/useInsuranceSaleSendPaymentLink";
import CustomPagination from "../../../../components/custom-component/CustomPagination";
import { DataTable, FilteredEmpty, MobileFilterBar, RowActionsMenu, useTableFilters, TruncatedText } from "../../../../components/data-table";
import InsuranceSaleInvoiceModal from "./InsuranceSaleInvoiceModal";
import InsuranceSaleMobileCard from "./InsuranceSaleMobileCard";
import InsuranceSaleDetailsModal from "./InsuranceSaleDetailsModal";

const SEARCH_FILTER_KEY = "searchTerm";
const PROVIDER_FILTER_KEY = "insuranceType";
const PAYMENT_FILTER_KEY = "isPaid";

const formatDate = (value) => (value ? moment(value).format("MM/DD/YYYY h:mm A") : "—");

const getStatusColor = (status) => {
    const normalized = String(status ?? "").toLowerCase();
    if (normalized === "active") return "success";
    if (normalized === "failed") return "error";
    if (normalized === "ended" || normalized === "completed") return "blue";
    return "default";
};

const getPolicyStatusLabel = (status, t) => {
    const normalized = String(status ?? "").toLowerCase();
    if (normalized === "failed") return t("invoice.paymentFailed");
    if (normalized === "active") return t("table.policyActive");
    if (normalized === "pending") return t("invoice.paymentPending");
    if (!status) return t("table.notStarted");
    return String(status).replace(/_/g, " ");
};

const InsuranceSalesTable = ({
    sales,
    loading,
    currentPage,
    pageSize,
    totalItems,
    setCurrentPage,
    setPageSize,
    searchParams,
    onSearchParamsChange,
    onPay,
    onRetry,
    onExtend,
    onEnd,
    onRefresh,
    isPast,
}) => {
    const { t } = useTranslation("sellInsurance");
    const toast = useToast();
    const { sendPaymentLink, loading: sendLinkLoading, bookingAgentConfigured } =
        useInsuranceSaleSendPaymentLink();
    const [downloadingId, setDownloadingId] = useState(null);
    const [sendingLinkId, setSendingLinkId] = useState(null);
    const [invoiceSale, setInvoiceSale] = useState(null);
    const [detailsSale, setDetailsSale] = useState(null);
    const table = useTableFilters({
        value: searchParams,
        onChange: (next) => {
            setCurrentPage(1);
            onSearchParamsChange(next);
        },
    });

    const providerOptions = [
        { label: PROVIDER_LABELS.teqmobility, value: "teqMobility" },
        { label: PROVIDER_LABELS.bonzah, value: "bonzah" },
    ];
    const paymentOptions = [
        { label: t("table.paid"), value: "true" },
        { label: t("table.unpaid"), value: "false" },
    ];
    const filterLabels = {
        [SEARCH_FILTER_KEY]: t("table.search"),
        [PROVIDER_FILTER_KEY]: t("table.provider"),
        [PAYMENT_FILTER_KEY]: t("table.payment"),
    };
    const formatFilterValue = (key, value) => {
        if (key === PROVIDER_FILTER_KEY) return providerOptions.find((o) => o.value === value)?.label;
        if (key === PAYMENT_FILTER_KEY) return paymentOptions.find((o) => o.value === value)?.label;
        return undefined;
    };
    const renderProviderSelect = (props) => (
        <Select
            allowClear
            placeholder={t("table.filterProvider")}
            value={searchParams[PROVIDER_FILTER_KEY]}
            onChange={(value) => table.setFilter(PROVIDER_FILTER_KEY, value)}
            options={providerOptions}
            {...props}
        />
    );
    const renderPaymentSelect = (props) => (
        <Select
            allowClear
            placeholder={t("table.filterPayment")}
            value={searchParams[PAYMENT_FILTER_KEY]}
            onChange={(value) => table.setFilter(PAYMENT_FILTER_KEY, value)}
            options={paymentOptions}
            {...props}
        />
    );

    const handleDownload = async (record) => {
        const policy = getPolicy(record);
        const provider = getProviderKey(record);

        if (provider === "teqmobility") {
            if (!policy?.cardUrl) {
                toast.error({ message: t("table.noCard") });
                return;
            }
            window.open(policy.cardUrl, "_blank", "noopener,noreferrer");
            return;
        }

        setDownloadingId(record.id);
        try {
            await downloadBonzahPolicyPdfFile(
                record.id,
                `bonzah-policy-${record.id}.pdf`,
            );
        } catch (error) {
            let description = error?.message || t("table.downloadError");
            if (error?.response?.data instanceof Blob) {
                const parsed = await parseBlobErrorMessage(error.response.data);
                if (parsed) description = parsed;
            }

            toast.error({
                message: t("table.downloadError"),
                description,
            });
        } finally {
            setDownloadingId(null);
        }
    };

    const handleSendPaymentLink = async (record) => {
        setSendingLinkId(record.id);
        try {
            await sendPaymentLink({
                reservationId: record.id,
                amount: Number(record.invoice?.due ?? record.invoice?.total ?? 0),
                customerEmail: record.email,
                reservationProcess: "insuranceSale",
                onSuccess: onRefresh,
            });
        } finally {
            setSendingLinkId(null);
        }
    };

    const buildActionItems = (record, { forMobile = false } = {}) => {
        const paid = isSalePaid(record);
        const status = getSalePolicyStatus(record);
        const provider = getProviderKey(record);
        const canRetry = paid && (!status || status === "failed" || status === "pending");
        const canExtend = paid && status === "active" && provider === "bonzah";
        const canEnd = status === "active" || status === "failed";

        const items = [];

        if (forMobile) {
            items.push({
                label: t("table.viewDetails"),
                key: "viewDetails",
                icon: <EyeOutlined />,
            });
        }

        // Past sales are closed: only their documents are still useful.
        if (isPast) {
            items.push({ label: t("table.downloadInvoice"), key: "invoice", icon: <FileTextOutlined /> });
            items.push({
                label: t("table.download"),
                key: "download",
                icon: <DownloadOutlined />,
                disabled: downloadingId === record.id,
            });
            return items;
        }

        if (!paid) {
            items.push({ label: t("table.pay"), key: "pay", icon: <DollarOutlined /> });
            items.push({
                label: t("pay.sendPaymentLink"),
                key: "sendLink",
                icon: <MailOutlined />,
                disabled:
                    !bookingAgentConfigured ||
                    sendingLinkId === record.id ||
                    sendLinkLoading,
            });
        }
        if (canRetry) {
            items.push({ label: t("table.retry"), key: "retry", icon: <ReloadOutlined /> });
        }
        if (canExtend) {
            items.push({ label: t("table.extend"), key: "extend", icon: <FieldTimeOutlined /> });
        } else if (paid && status === "active" && provider === "teqmobility") {
            items.push({
                label: t("table.extend"),
                key: "extendDisabled",
                icon: <FieldTimeOutlined />,
                disabled: true,
            });
        }
        if (canEnd) {
            items.push({
                label: t("table.end"),
                key: "end",
                icon: <StopOutlined />,
                danger: true,
            });
        }
        items.push({
            label: t("table.downloadInvoice"),
            key: "invoice",
            icon: <FileTextOutlined />,
        });
        items.push({
            label: t("table.download"),
            key: "download",
            icon: <DownloadOutlined />,
            disabled: downloadingId === record.id,
        });

        return items;
    };

    const handleAction = (record, key) => {
        if (key === "viewDetails") {
            setDetailsSale(record);
            return;
        }
        if (key === "pay") {
            onPay?.(record);
            return;
        }
        if (key === "sendLink") {
            handleSendPaymentLink(record);
            return;
        }
        if (key === "retry") {
            onRetry?.(record);
            return;
        }
        if (key === "extend") {
            onExtend?.(record);
            return;
        }
        if (key === "end") {
            onEnd?.(record);
            return;
        }
        if (key === "invoice") {
            setInvoiceSale(record);
            return;
        }
        if (key === "download") {
            handleDownload(record);
        }
    };

    const columns = [
        {
            title: t("table.saleId"),
            dataIndex: "id",
            key: "id",
            width: 130,
            render: (value) => <span className="sc-mono">{value}</span>,
        },
        {
            title: t("table.customer"),
            key: "customer",
            width: 200,
            ...table.getColumnSearchProps(SEARCH_FILTER_KEY, { placeholder: t("searchPlaceholder") }),
            render: (_, record) => (
                <div className="flex flex-col leading-tight">
                    <TruncatedText className="font-medium text-gray-900">{record.customerName || "—"}</TruncatedText>
                    <TruncatedText className="text-xs text-gray-500">{record.email}</TruncatedText>
                </div>
            ),
        },
        {
            title: t("table.vehicle"),
            key: "vehicle",
            width: 180,
            render: (_, record) => (
                <div className="flex flex-col leading-tight">
                    <TruncatedText>{record.carName || "—"}</TruncatedText>
                    {record.carSelected?.VIN && (
                        <Link to={`/cardetails/${record.carSelected.VIN}`} className="sc-table-link sc-mono !text-xs">
                            {record.carSelected.VIN}
                        </Link>
                    )}
                </div>
            ),
        },
        {
            title: t("table.provider"),
            key: "provider",
            width: 120,
            ...table.getColumnSelectProps(PROVIDER_FILTER_KEY, providerOptions),
            render: (_, record) => PROVIDER_LABELS[getProviderKey(record)] ?? "—",
        },
        {
            title: t("table.period"),
            key: "period",
            width: 220,
            render: (_, record) => (
                <div className="text-xs">
                    <div>{formatDate(record.pickupDate)}</div>
                    <div className="text-gray-500">{formatDate(record.dropoffDate)}</div>
                </div>
            ),
        },
        {
            title: t("table.policyStatus"),
            key: "policyStatus",
            width: 140,
            render: (_, record) => {
                const policy = getPolicy(record);
                const status = policy?.status;
                const normalizedStatus = String(status ?? "").toLowerCase();
                const statusTag = (
                    <Tag color={getStatusColor(status)} className="rounded-full px-3">
                        {getPolicyStatusLabel(status, t)}
                    </Tag>
                );

                return policy?.lastError && normalizedStatus === "failed" ? (
                    <Tooltip title={policy.lastError}>{statusTag}</Tooltip>
                ) : (
                    statusTag
                );
            },
        },
        {
            title: t("table.payment"),
            key: "payment",
            width: 130,
            ...table.getColumnSelectProps(PAYMENT_FILTER_KEY, paymentOptions),
            render: (_, record) => {
                const paid = isSalePaid(record);
                return (
                    <div>
                        <Tag color={paid ? "success" : "warning"} className="rounded-full px-3">
                            {paid ? t("table.paid") : t("table.unpaid")}
                        </Tag>
                        {!paid && (
                            <div className="mt-1 text-xs text-gray-500">
                                {formatCurrency(record.invoice?.due ?? record.invoice?.total)}
                            </div>
                        )}
                    </div>
                );
            },
        },
        {
            title: t("table.total"),
            key: "total",
            width: 110,
            align: "right",
            render: (_, record) => formatCurrency(record.invoice?.total),
        },
        isPast
            ? {
                  title: t("table.endedOn"),
                  dataIndex: "exactDropoffTime",
                  key: "exactDropoffTime",
                  width: 130,
                  render: (value) => (value ? moment(value).format("MM/DD/YYYY") : "—"),
              }
            : {
                  title: t("table.soldOn"),
                  dataIndex: "createdAt",
                  key: "createdAt",
                  width: 130,
                  render: (value) => (value ? moment(value).format("MM/DD/YYYY") : "—"),
              },
        {
            title: <span className="sr-only">{t("table.actions")}</span>,
            key: "actions",
            width: 64,
            align: "center",
            fixed: "right",
            render: (_, record) => {
                const actionItems = buildActionItems(record);

                if (actionItems.length === 0) {
                    return "—";
                }

                return (
                    <RowActionsMenu
                        items={actionItems}
                        onClick={({ key }) => handleAction(record, key)}
                        label={t("table.actions")}
                    />
                );
            },
        },
    ];

    const mobileSalesList = sales.map((item, index) => ({ ...item, key: item.id ?? index }));

    const renderMobileFilters = () => (
        <div className="mt-4">
            <MobileFilterBar
                filters={table}
                search={{ key: SEARCH_FILTER_KEY, placeholder: t("searchPlaceholder") }}
                filterLabels={filterLabels}
                formatFilterValue={formatFilterValue}
                extra={
                    <>
                        {renderProviderSelect({ size: "large" })}
                        {renderPaymentSelect({ size: "large" })}
                    </>
                }
            />
        </div>
    );

    const renderMobileCards = () => {
        if (loading) {
            return (
                <div className="flex justify-center py-12">
                    <Spin />
                </div>
            );
        }

        if (!mobileSalesList.length) {
            return (
                <div className="py-12">
                    <FilteredEmpty filters={table} description={isPast ? t("table.emptyPast") : t("table.empty")} />
                </div>
            );
        }

        return (
            <div className="mt-4 flex flex-col gap-3">
                {mobileSalesList.map((record) => (
                    <InsuranceSaleMobileCard
                        key={record.id}
                        record={record}
                        t={t}
                        actionItems={buildActionItems(record, { forMobile: true })}
                        onAction={handleAction}
                        getStatusColor={getStatusColor}
                        getPolicyStatusLabel={getPolicyStatusLabel}
                    />
                ))}
            </div>
        );
    };

    return (
        <div className="min-w-0">
            <div className="flex min-w-0 flex-col gap-4 lg:hidden">
                {renderMobileFilters()}
                {renderMobileCards()}
            </div>
            <div className="hidden min-w-0 lg:block">
                <DataTable
                    filters={table}
                    search={{ key: SEARCH_FILTER_KEY, placeholder: t("searchPlaceholder") }}
                    toolbarExtra={
                        <>
                            {renderProviderSelect({ className: "min-w-40" })}
                            {renderPaymentSelect({ className: "min-w-40" })}
                        </>
                    }
                    filterLabels={filterLabels}
                    formatFilterValue={formatFilterValue}
                    columns={columns}
                    dataSource={mobileSalesList}
                    loading={loading}
                    rowKey="id"
                    scroll={{ x: 1400 }}
                    emptyText={
                        <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description={isPast ? t("table.emptyPast") : t("table.empty")}
                        />
                    }
                    pagination={{
                        total: totalItems,
                        current: currentPage,
                        pageSize,
                        onChange: setCurrentPage,
                        onPageSizeChange: setPageSize,
                        pageSizeOptions: [10, 20, 30, 50],
                    }}
                />
            </div>

            <div className="mt-4 flex min-w-0 w-full justify-end lg:hidden">
                <div className="w-full">
                    <CustomPagination
                        variant="spaced"
                        totalItem={totalItems}
                        pageSize={pageSize}
                        setPageSize={setPageSize}
                        currentPage={currentPage}
                        setCurrentPage={setCurrentPage}
                        pageSizeOptions={[10, 20, 30, 50]}
                        showSizeChanger
                    />
                </div>
            </div>

            <InsuranceSaleInvoiceModal
                open={Boolean(invoiceSale)}
                sale={invoiceSale}
                onClose={() => setInvoiceSale(null)}
                onRefresh={onRefresh}
            />

            <InsuranceSaleDetailsModal
                open={Boolean(detailsSale)}
                sale={detailsSale}
                onClose={() => setDetailsSale(null)}
                t={t}
                getStatusColor={getStatusColor}
                getPolicyStatusLabel={getPolicyStatusLabel}
            />
        </div>
    );
};

InsuranceSalesTable.propTypes = {
    sales: PropTypes.arrayOf(PropTypes.object).isRequired,
    loading: PropTypes.bool,
    currentPage: PropTypes.number.isRequired,
    pageSize: PropTypes.number.isRequired,
    totalItems: PropTypes.number.isRequired,
    setCurrentPage: PropTypes.func.isRequired,
    setPageSize: PropTypes.func.isRequired,
    searchParams: PropTypes.object.isRequired,
    onSearchParamsChange: PropTypes.func.isRequired,
    onPay: PropTypes.func,
    onRetry: PropTypes.func,
    onExtend: PropTypes.func,
    onEnd: PropTypes.func,
    onRefresh: PropTypes.func,
    isPast: PropTypes.bool,
};

InsuranceSalesTable.defaultProps = {
    loading: false,
    onPay: null,
    onRetry: null,
    onExtend: null,
    onEnd: null,
    onRefresh: null,
    isPast: false,
};

export default InsuranceSalesTable;
